import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQuery } from "@tanstack/react-query";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { saveOnboardingStep, createService, getCategories } from "../../api/auth";
import { trackOnboardingStepComplete } from "../../lib/analytics";

interface ServiceEntry {
  service_name: string;
  description: string;
  duration_minutes: number;
  price: number;
  category_id?: string;
}

export default function ServicesStep() {
  const { setCurrentStep, setStepData, setSaving, saving } = useOnboardingStore();
  const [services, setServices] = useState<ServiceEntry[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState<number>(60);
  const [price, setPrice] = useState<number>(0);
  const [categoryId, setCategoryId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const { data: categories = [] } = useQuery({
    queryKey: ["onboarding-categories"],
    queryFn: getCategories,
    staleTime: Infinity,
  });

  const addService = () => {
    if (!name.trim()) return;
    setServices((s) => [
      ...s,
      {
        service_name: name.trim(),
        description: description.trim(),
        duration_minutes: duration,
        price,
        category_id: categoryId || undefined,
      },
    ]);
    setName("");
    setDescription("");
    setDuration(60);
    setPrice(0);
    setCategoryId("");
  };

  const removeService = (idx: number) => setServices((s) => s.filter((_, i) => i !== idx));

  const handleContinue = async () => {
    setError(null);
    setSaving(true);
    try {
      for (const svc of services) {
        await createService(svc);
      }
      setStepData("services", services.map((s) => ({
        name: s.service_name,
        description: s.description,
        duration_minutes: s.duration_minutes,
        price: s.price,
      })));
      await saveOnboardingStep({ step: "services", step_status: services.length > 0 ? "complete" : "skipped" });
      trackOnboardingStepComplete(3, "services", true);
      setCurrentStep(4);
    } catch {
      setError("Failed to save services. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await saveOnboardingStep({ step: "services", step_status: "skipped" });
      trackOnboardingStepComplete(3, "services", true);
      setCurrentStep(4);
    } catch {
      setError("Failed to skip services. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const categoryName = (id: string) => categories.find((c) => c.service_category_id === id)?.name ?? "";

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Add your services</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add the services you offer. You can add more later in Settings.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Service name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: "1 1 180px" }}
          size="small"
        />
        <TextField
          label="Service description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ flex: "1 1 100%" }}
          size="small"
          multiline
          minRows={2}
          placeholder="What does this service include, who is it for, or what should the AI mention?"
        />
        {categories.length > 0 && (
          <TextField
            select
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            sx={{ width: 150 }}
            size="small"
          >
            <MenuItem value="">— none —</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.service_category_id} value={c.service_category_id}>{c.name}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Duration (min)"
          type="number"
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          sx={{ width: 120 }}
          size="small"
          inputProps={{ min: 5, step: 5 }}
        />
        <TextField
          label="Price ($)"
          type="number"
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          sx={{ width: 100 }}
          size="small"
          inputProps={{ min: 0, step: 0.5 }}
        />
        <Button variant="outlined" onClick={addService} disabled={!name.trim()}>
          Add
        </Button>
      </Box>

      {services.length > 0 && (
        <List dense sx={{ mb: 3, border: 1, borderColor: "divider", borderRadius: 1 }}>
          {services.map((s, i) => (
            <ListItem key={i} divider={i < services.length - 1}>
              <ListItemText
                primary={s.service_name}
                secondary={[
                  s.description,
                  `${s.duration_minutes} min · $${s.price}${s.category_id ? ` · ${categoryName(s.category_id)}` : ""}`,
                ].filter(Boolean).join(" · ")}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => removeService(i)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="contained" onClick={handleContinue} disabled={saving} sx={{ flex: 1 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Continue"}
        </Button>
        <Button variant="text" onClick={handleSkip} disabled={saving}>
          Skip for now
        </Button>
      </Box>
    </Box>
  );
}
